import React, { useState } from "react"
import { createPortal } from "react-dom"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../constants"
import { TextLogoView } from "../sitewide-styles/components/TextLogoView"
import { getThemeColors } from "../utils/colorModeUtils"
import { getContrastSafeButtonTextColor, isDarkColor } from "../utils/colorUtils"
import { formatFontFamily } from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  buildServiceUrl,
  buildBookingUrl,
  buildEventTypeUrl,
  buildDefaultPageUrl,
  findBuilderPageByDefaultType,
  mergeBuilderNavLinks,
  normalizeBuilderNavHref,
  openExternalUrl,
  pageVisibleInBuilderNav,
  publicPathForBuilderPage,
  scopePath
} from "../utils/urlUtils"
import { useOptionalWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

const HeaderSectionMobile = ({
  section,
  practice,
  theme,
  highlightedField,
  transparent = false,
  transparentBackgroundColor
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Context is null on public page (no Provider); use practice pages in that case
  const context = useOptionalWebsiteBuilderV2()
  const contextPages = context?.pages || []
  const textLogoBuilderEnabled = context?.textLogoBuilderEnabled ?? false
  const practicePages = practice?.pages || []
  const availablePages = contextPages.length > 0 ? contextPages : practicePages

  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const showLogo = settings.showLogo || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || theme?.header?.buttonText || "Book Now"
  const ctaAction = theme?.header?.ctaAction || settings.ctaAction || "booking"
  const ctaTarget = theme?.header?.ctaTarget || settings.ctaTarget || ""
  const externalUrl = theme?.header?.externalUrl || settings.externalUrl || ""
  const showNavigation = settings.showNavigation || true

  // Promo banner settings from HeaderForm
  const showPromoBanner = theme?.header?.showPromoBanner || false
  const promoBannerText = theme?.header?.promoHeadline || "This is some exciting new event that we are offering!"
  const promoBannerLinkText = theme?.header?.promoButtonText || "Learn more"
  const promoBannerAction = theme?.header?.promoCtaType || "page"
  const promoBannerTarget = theme?.header?.promoService || ""
  const promoBannerEvent = theme?.header?.promoEvent || ""
  const promoExternalUrl = theme?.header?.promoExternalUrl || ""
  const promoBannerColorMode = theme?.header?.promoBannerColorMode || "white"

  // Get theme colors for promo banner
  const themeColors = getThemeColors(theme)

  // Resolve promo banner colors based on color mode
  const getPromoBannerColors = () => {
    switch (promoBannerColorMode) {
      case "dark":
        return {
          backgroundColor: themeColors.primary || "#BD654F",
          textColor: themeColors.textLight || "#FFFFFF",
          linkColor: themeColors.textLight || "#FFFFFF"
        }
      case "light":
        return {
          backgroundColor: themeColors.bgLight || "#F4ECE9",
          textColor: themeColors.textDark || "#55433F",
          linkColor: themeColors.primary || "#BD654F"
        }
      case "white":
      default:
        return {
          backgroundColor: "#FFFFFF",
          textColor: themeColors.textDark || "#55433F",
          linkColor: themeColors.primary || "#BD654F"
        }
    }
  }

  const promoBannerColors = getPromoBannerColors()

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${ctaTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (externalUrl || ctaTarget) {
          const url = externalUrl || ctaTarget
          if (url) window.open(url, "_blank", "noopener,noreferrer")
        }
        break
      default:
        break
    }
  }

  const handlePromoBannerClick = () => {
    switch (promoBannerAction) {
      case "page":
        if (promoBannerTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${promoBannerTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (promoBannerTarget) {
          // It's a service, use the service URL
          window.location.href = buildServiceUrl(promoBannerTarget, practice)
        }
        break
      case "event":
        if (promoBannerEvent) {
          // Build event URL using the event type slug
          const eventUrl = buildEventTypeUrl(promoBannerEvent, practice)
          window.location.href = eventUrl
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        openExternalUrl(promoExternalUrl)
        break
      default:
        break
    }
  }

  const appendBlogNavLink = (links) => {
    const hasBlog = practice?.hasPublishedBlogPosts || practice?.has_published_blog_posts
    const blogLabel = practice?.blogPageTitle || practice?.blog_page_title || "Blog"
    const blogHref = buildDefaultPageUrl(practice, "blog", "blog")
    if (!hasBlog || links.some((link) => link.href === blogHref)) return links

    return [...links, { text: blogLabel, href: blogHref }]
  }

  const fallbackDefaultNavPages = [
    { defaultPageType: "services", label: "Services", legacySlug: "services" },
    { defaultPageType: "about", label: "About", legacySlug: "about" },
    { defaultPageType: "reviews", label: "Reviews", legacySlug: "review" },
    { defaultPageType: "contact", label: "Contact", legacySlug: "contact" }
  ]

  // Generate navigation links from practice's pages
  const generateNavigationLinks = () => {
    if (!availablePages || availablePages.length === 0) {
      const defaultLinks = fallbackDefaultNavPages.map(({ defaultPageType, label, legacySlug }) => {
        const page = findBuilderPageByDefaultType(practice, defaultPageType)
        return {
          text: page?.title || label,
          href: buildDefaultPageUrl(practice, defaultPageType, legacySlug)
        }
      })

      // Add Home link only if showHome is enabled
      const showHome = theme?.header?.showHome || false
      const links = showHome ? [{ text: "Home", href: scopePath("/", practice) }, ...defaultLinks] : defaultLinks
      return appendBlogNavLink(links)
    }

    // Start with Home link if enabled, then add other pages
    const showHome = theme?.header?.showHome || false
    const homeLink = showHome ? { text: "Home", href: scopePath("/", practice) } : null

    // Filter out the home page and hidden pages, then create navigation links from available pages
    const pageLinks = availablePages
      .filter((page) => page.defaultPageType !== "home" && pageVisibleInBuilderNav(page, practice))
      .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order (similar to sections)
      .map((page) => ({
        text: page.title,
        href: publicPathForBuilderPage(page, practice)
      }))

    const links = homeLink ? [homeLink, ...pageLinks] : pageLinks
    return appendBlogNavLink(links)
  }

  const navigationLinks = generateNavigationLinks()

  // Merge legacy stored nav with dynamic page links so custom pages appear like footer nav.
  const headerNavigationLinks = mergeBuilderNavLinks(theme?.header?.navigationLinks, navigationLinks, practice)

  // Helper function to determine if a link is active
  const isLinkActive = (href) => {
    if (typeof window === "undefined") return false
    const currentPath = window.location.pathname
    // Normalize paths: remove leading/trailing slashes and convert to arrays
    const normalizePath = (path) =>
      (typeof path === "string" ? path : "")
        .replace(/^\/+|\/+$/g, "")
        .split("/")
        .filter(Boolean)
    const currentParts = normalizePath(currentPath)
    const hrefParts = normalizePath(href)

    // If href is empty or just "/", check if we're exactly on home (no sub-routes)
    if (hrefParts.length === 0) {
      return currentParts.length === 0
    }

    // Special case: if href is just the practice slug (e.g., "/peaceful-living"),
    // it should only be active when we're exactly on that path, not on sub-routes
    // This handles the "Home" link case
    if (hrefParts.length === 1) {
      // Check if this looks like a home link (just practice slug)
      // If current path has more parts, it's not the home page
      if (currentParts.length !== 1) return false
      // Compare the single segment
      return currentParts[0] === hrefParts[0]
    }

    // For other links with multiple segments, check if current path starts with href path
    if (currentParts.length < hrefParts.length) return false

    // Compare path segments
    for (let i = 0; i < hrefParts.length; i++) {
      if (currentParts[i] !== hrefParts[i]) return false
    }

    return true
  }

  // Helper function to get navigation link styles based on state (for mobile menu)
  const getNavLinkStyles = (link, isActive) => {
    const baseStyles = {
      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
      fontSize: "24px",
      lineHeight: "32px",
      textDecoration: "none",
      transition: "all 0.2s ease"
    }

    if (isActive) {
      return {
        ...baseStyles,
        fontWeight: "600",
        color: theme?.colors?.textDark || "#55433f",
        borderBottom: `2px solid ${theme?.colors?.primary || "#bd654f"}`
      }
    }
    return {
      ...baseStyles,
      fontWeight: "400",
      color: theme?.colors?.textDark || "#55433f"
    }
  }

  // Helper function to get hover styles for mobile
  const getNavLinkHoverStyles = (isActive) => {
    if (isActive) return {} // Active links already have underline
    return {
      borderBottom: `1px solid ${theme?.colors?.primary || "#bd654f"}`
    }
  }

  // Determine text color for transparent header based on background
  // When transparent, check if the background behind is light or dark
  const getTransparentTextColor = () => {
    if (!transparent) return null

    // If caller tells us what’s behind the transparent header, use it.
    // Otherwise, keep legacy behavior: transparent headers are assumed to sit on dark hero imagery.
    if (!transparentBackgroundColor) return theme?.colors?.textLight || "#FFFFFF"

    const isBackgroundDark = isDarkColor(transparentBackgroundColor)
    return isBackgroundDark ? theme?.colors?.textLight || "#FFFFFF" : theme?.colors?.textDark || "#55433f"
  }

  const transparentTextColor = getTransparentTextColor()
  const menuIconColor =
    transparent && transparentTextColor
      ? transparentTextColor
      : transparent
      ? theme?.colors?.textLight || "#FFFFFF"
      : theme?.colors?.textDark || "#000000"

  return (
    <div className="relative z-10">
      {/* Promo Banner - Separate from header section */}
      {showPromoBanner && (
        <div
          className="flex w-full items-center justify-center px-6 py-3 text-center"
          style={{
            backgroundColor: promoBannerColors.backgroundColor,
            color: promoBannerColors.textColor
          }}>
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-normal leading-5"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontSize: "14px",
                lineHeight: "20px"
              }}>
              {promoBannerText}{" "}
              <button
                onClick={handlePromoBannerClick}
                className="font-semibold underline hover:no-underline"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: promoBannerColors.linkColor
                }}>
                {promoBannerLinkText}
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Main Header Section */}
      <section
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          "header-section-mobile relative flex items-center",
          transparent ? "bg-transparent" : "",
          isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
        )}
        style={{
          backgroundColor: transparent ? "transparent" : theme?.colors?.background || "#FFFFFF",
          minHeight: "60px",
          height: showLogo ? "56px" : "60px" // 40px max logo + 16px padding
        }}>
        <AnimatedSection className="w-full px-4 py-2">
          <div className="flex w-full items-center justify-between">
            {/* Left Side - Logo */}
            {showLogo && (
              <div className="flex items-center">
                <a href={scopePath("/", practice)} className="cursor-pointer transition-opacity hover:opacity-75">
                  {/* Text logo only when flag on and theme has logoType/textLogo; existing themes keep image/name */}
                  {textLogoBuilderEnabled && theme?.header?.logoType === "text" && theme?.header?.textLogo ? (
                    (() => {
                      const tl = theme?.header?.textLogo
                      const logoSize = theme?.header?.logoSize ?? 72
                      // Honor user-picked font sizes verbatim. Only the fallback (legacy
                      // themes without explicit sizes) is derived from logoSize.
                      const mainFontSize = tl?.mainFontSize ?? Math.round(logoSize * 0.18)
                      const secondFontSize = tl?.secondFontSize
                      return (
                        <TextLogoView
                          mainLine={tl?.mainLine || practice?.name || "Practice Name"}
                          secondLine={tl?.secondLine}
                          mainFont={tl?.mainFont || "Inter"}
                          secondFont={tl?.secondFont || "Inter"}
                          color={
                            transparent && transparentTextColor
                              ? transparentTextColor
                              : theme?.colors?.textDark || "#000000"
                          }
                          mainFontSize={`${mainFontSize}px`}
                          secondFontSize={secondFontSize != null ? `${secondFontSize}px` : undefined}
                          mainLetterSpacing={tl?.mainLetterSpacing}
                          secondLetterSpacing={tl?.secondLetterSpacing}
                          lineSpacing={tl?.lineSpacing}
                          alignment={tl?.alignment}
                        />
                      )
                    })()
                  ) : theme?.header?.logo ? (
                    <img
                      src={theme?.header?.logo}
                      alt={practice?.name || "Practice Logo"}
                      width={52}
                      height={40}
                      className="w-auto object-contain"
                      style={{
                        maxWidth: `${(theme?.header?.logoSize || 72) * 0.8}px`,
                        maxHeight: "40px",
                        height: "auto",
                        width: "auto",
                        borderRadius: theme?.borderRadius || "0px"
                      }}
                    />
                  ) : (
                    <span
                      className="text-xs font-bold tracking-tight"
                      style={{
                        fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                        fontWeight: "700",
                        fontSize: "12px",
                        lineHeight: "1em",
                        letterSpacing: "-4%",
                        color:
                          transparent && transparentTextColor
                            ? transparentTextColor
                            : theme?.colors?.textDark || "#000000"
                      }}>
                      {practice?.name || "Practice Name"}
                    </span>
                  )}
                </a>
              </div>
            )}
            {/* Right Side - Mobile Menu Button */}
            <div className="flex items-center">
              {/* Mobile Menu Button */}
              {showNavigation && (
                <button
                  type="button"
                  aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex size-8 items-center justify-center"
                  style={{ color: menuIconColor }}>
                  <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isMobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              )}
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* Mobile Menu Dropdown - Portaled to body so it always stacks above hero/sections */}
      {isMobileMenuOpen &&
        showNavigation &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              style={{ zIndex: 2147483001 }}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            {/* Menu Content */}
            <div
              className="fixed left-0 top-0 flex h-screen w-full flex-col overflow-y-auto"
              style={{
                backgroundColor: theme?.colors?.background || "#FFFFFF",
                zIndex: 2147483002,
                overflowX: "hidden"
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu">
              {/* Header - Logo and Close Button */}
              <div
                className="flex items-center justify-between p-4"
                style={{
                  zIndex: 2147483003,
                  position: "relative",
                  minHeight: "56px",
                  width: "100%"
                }}>
                {/* Logo - Always show in mobile menu for better UX */}
                {true && (
                  <a
                    href={scopePath("/", practice)}
                    className="cursor-pointer transition-opacity hover:opacity-75"
                    style={{
                      zIndex: 2147483004,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      minWidth: "100px"
                    }}>
                    {theme?.header?.logo || practice?.logoUrl ? (
                      <img
                        src={theme?.header?.logo || practice?.logoUrl}
                        alt={practice?.name || "Practice Logo"}
                        width={52}
                        height={40}
                        className="w-auto object-contain"
                        style={{
                          maxWidth: `${(theme?.header?.logoSize || 72) * 0.8}px`,
                          maxHeight: "40px",
                          height: "auto",
                          width: "auto",
                          borderRadius: theme?.borderRadius || "0px",
                          position: "relative",
                          zIndex: 2147483005,
                          display: "block",
                          visibility: "visible",
                          opacity: 1,
                          flexShrink: 0
                        }}
                        onError={(e) => {
                          console.warn("Logo failed to load:", theme?.header?.logo || practice?.logoUrl)
                          // Replace image with text fallback
                          const parent = e.target.parentElement
                          if (parent && !parent.querySelector(".logo-fallback-text")) {
                            e.target.style.display = "none"
                            const fallback = document.createElement("span")
                            fallback.className = "logo-fallback-text text-xs font-bold tracking-tight"
                            fallback.style.cssText = `
                            font-family: ${theme?.fonts?.heading || "Playfair Display, serif"};
                            font-weight: 700;
                            font-size: 12px;
                            line-height: 1em;
                            letter-spacing: -4%;
                            color: ${theme?.colors?.textDark || "#000000"};
                            position: relative;
                            z-index: 2147483005;
                            display: block;
                          `
                            fallback.textContent = practice?.name || "Practice Name"
                            parent.appendChild(fallback)
                          }
                        }}
                      />
                    ) : (
                      <span
                        className="logo-fallback-text text-xs font-bold tracking-tight"
                        style={{
                          fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                          fontWeight: "700",
                          fontSize: "12px",
                          lineHeight: "1em",
                          letterSpacing: "-4%",
                          color: theme?.colors?.textDark || "#000000",
                          position: "relative",
                          zIndex: 2147483005,
                          display: "block",
                          visibility: "visible",
                          opacity: 1
                        }}>
                        {practice?.name || "Practice Name"}
                      </span>
                    )}
                  </a>
                )}

                {/* Close Button */}
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex size-10 items-center justify-center"
                  style={{ color: theme?.colors?.textDark || "#000000" }}>
                  <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Navigation Links and CTA - Centered Vertically */}
              <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4">
                <nav className="flex flex-col items-center justify-center gap-6">
                  {headerNavigationLinks.map((link, index) => {
                    const navHref = normalizeBuilderNavHref(link.href, practice)
                    const active = isLinkActive(navHref)
                    const baseStyles = getNavLinkStyles(link, active)
                    return (
                      <a
                        key={index}
                        href={navHref}
                        onClick={() => {
                          // Close menu when navigating
                          setIsMobileMenuOpen(false)
                        }}
                        className="text-center transition-all"
                        style={baseStyles}
                        onMouseEnter={(e) => {
                          if (!active) {
                            const hoverStyles = getNavLinkHoverStyles(active)
                            Object.assign(e.target.style, hoverStyles)
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            e.target.style.borderBottom = "none"
                          }
                        }}>
                        {link.text}
                      </a>
                    )
                  })}
                </nav>

                {/* Main CTA - Centered, not full width */}
                {showCTA && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCTAClick()
                      setIsMobileMenuOpen(false)
                    }}
                    className={twMerge(
                      "inline-flex items-center justify-center px-12 py-3 text-base font-normal transition-all hover:shadow-lg",
                      getButtonClasses(theme?.buttonStyle)
                    )}
                    style={applyButtonTheme(
                      {
                        backgroundColor: theme?.colors?.primary || "#BD654F",
                        color: getContrastSafeButtonTextColor(theme?.colors?.primary || "#BD654F", theme),
                        fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                        fontWeight: "400",
                        fontSize: "16px",
                        lineHeight: "24px"
                      },
                      theme
                    )}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    }}>
                    {ctaText}
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default HeaderSectionMobile
